import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  IconButton,
  Paper,
  Tooltip,
  Collapse,
  TableContainer,
  TableCell,
  TableRow,
  makeStyles,
} from '@material-ui/core';

import FilterList from '@material-ui/icons/FilterList';
import Edit from '@material-ui/icons/Edit';
import AddBox from '@material-ui/icons/AddBox';
import Delete from '@material-ui/icons/Delete';

import RichText from 'common/components/RichText';
import ConfirmButton from 'common/components/ConfirmButton';
import { EnhancedTable } from 'common/components/tables';
import ToolBar from 'common/components/ToolBar';
import Loading from 'common/components/Loading';

import ListActions from 'public/redux/Questions/actions';
import CommonActions from 'public/redux/common/actions';
import QuestionSearchForm from './QuestionSearchForm';

const columnData = [
  { id: 'id', numeric: false, disablePadding: false, label: 'ID', sortable: true },
  { id: 'item_no', numeric: false, disablePadding: false, label: 'Item No.', sortable: true },
  { id: 'content', numeric: false, disablePadding: false, label: 'Content', sortable: true },
  { id: 'type', numeric: false, disablePadding: false, label: 'Type', sortable: true },
  { id: 'active', numeric: false, disablePadding: false, label: 'Status', sortable: true },
  { id: 'actions', numeric: false, disablePadding: false, label: '', sortable: false },
];

const useStyles = makeStyles(() => ({
  paper: { position: 'relative' }
}));


const renderStatus = (status) => {
  switch (Number(status)) {
    case 10:
      return 'Active';
    case 5:
      return 'Submitted';
    case 0:
    default:
      return 'Draft';
  }
};

const QuestionRow = ({
  rowData,
  selectedRows,
  setSelectedRows,
}) => {
  const dispatch = useDispatch();
  const isSelected = id => (selectedRows.findIndex(ele => ele.id === id) >= 0);
  const rowSelected = isSelected(rowData.id);
  const handleRowClick = () => {
    if (rowSelected) {
      setSelectedRows(selectedRows.filter(ele => ele.id !== rowData.id));
    } else {
      setSelectedRows(selectedRows.concat([rowData]));
    }
  };
  const handleDelete = React.useCallback(async () => {
    try {
      await dispatch(ListActions.delete(rowData));
      dispatch(CommonActions.setSnackbarMessage(`${rowData.id} is deleted..`));
    } catch (e) {
      dispatch(CommonActions.setSnackbarMessage(`Error: ${(e.response?.data?.message || e)}`));
    }
  }, [rowData, dispatch]);

  return (
    <TableRow
      hover
      tabIndex={-1}
      selected={rowSelected}
      aria-checked={rowSelected}>
      <TableCell padding="checkbox">
        <Checkbox color="primary" checked={rowSelected} onClick={handleRowClick} />
      </TableCell>
      <TableCell>{rowData.id}</TableCell>
      <TableCell>{rowData.item_no}</TableCell>
      <TableCell><RichText content={rowData.content} /></TableCell>
      <TableCell>{rowData.type}{(rowData.type === 'RATE') ? `-${rowData.max_rate}` : ''}</TableCell>
      <TableCell>{renderStatus(rowData.status)}</TableCell>
      <TableCell>
        <Tooltip title="View Details">
          <IconButton component={Link} to={`/staff/question/${rowData.id}`} aria-label="View Details"><Edit /></IconButton>
        </Tooltip>
        <Tooltip title="Delete Question">
          <ConfirmButton
            ButtonComponent={IconButton}
            onConfirm={handleDelete}
            message={`Confirm to delete question "${rowData.id}"?`}
          >
            <Delete />
          </ConfirmButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
const renderRow = ({ rowData, ...props }) => <QuestionRow key={`${rowData.id}`}  {...props} rowData={rowData} />;

const mapStateToProps = ({ Questions }) => Questions;
const QuestionList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { page, pageSize, data, totalElements, sort, form, init, submitting } = useSelector(mapStateToProps);
  const [formOpen, setFormOpen] = React.useState(true);

  const handleToggleFormOpen = React.useCallback(() => setFormOpen(!formOpen), [formOpen]);
  const handleSearch = React.useCallback((values) => dispatch(ListActions.searchFormSubmit(values)).catch(console.log), [dispatch]);
  const handleSortChange = React.useCallback(sort => dispatch(ListActions.sortChange(sort)).catch(console.log), [dispatch]);
  const handlePageChange = React.useCallback(page => dispatch(ListActions.pageChange(page)).catch(console.log), [dispatch]);

  React.useEffect(() => {
    if (!init && !submitting) {
      dispatch(ListActions.searchFormSubmit({})).catch(console.log);
    }
  }, [dispatch, init, submitting]);

  return (
    <div>
      <ToolBar
        color="default"
        title="Questions"
        rightButton={[
          { title: 'Filter list', icon: (<FilterList />), onClick: handleToggleFormOpen },
          { title: 'Add Question', icon: (<AddBox />), ButtonProps: { component: Link, to: '/staff/questions/create' } },
        ]}
      />
      <Collapse in={formOpen}>
        <QuestionSearchForm
          initialValues={form}
          onSubmit={handleSearch}
        />
      </Collapse>
      <Paper className={classes.paper}>
        <TableContainer>
          <EnhancedTable
            totalElements={totalElements}
            pageSize={pageSize}
            page={page}
            sort={sort}
            columnData={columnData}
            data={data}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            renderRow={renderRow}
          />
        </TableContainer>
        <Loading loading={submitting} />
      </Paper>
    </div>
  );
}

export default QuestionList;
